import { Button } from '@/components/custom/button'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogDescription,
  DialogTitle,
} from '@/components/ui/dialog'
import { Form, FormControl, FormField, FormItem } from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/custom/label'
import { useEffect, useRef, useState } from 'react'
import { Plus, Trash2 } from 'lucide-react'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import { useFieldArray, useForm } from 'react-hook-form'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { createGroupTag, updateGroupTag } from '@/api/settings'
import { toast } from 'sonner'
import { showErrorToast } from '@/components/ui/sonner'
import { cn } from '@/lib/utils'
import QUERY_KEY from '@/constants/use-query-key.js'

const formSchema = z.object({
  name: z.string().refine((v) => v.trim() !== '', {
    message: 'Tên nhóm tags không được bỏ trống',
  }),
  tags: z.array(
    z.object({
      tag: z.string().refine((v) => v.trim() !== '', {
        message: 'Tên tag không được bỏ trống',
      }),
    }),
  ),
})

export default function DialogGroupTag({ open, editGroupTag, handleClose }) {
  const [focusFieldIndex, setFocusFieldIndex] = useState(-1)
  const queryClient = useQueryClient()
  const formRef = useRef(null)
  const form = useForm({
    resolver: zodResolver(formSchema),
    mode: 'onSubmit',
    defaultValues: {
      name: '',
      tags: [],
    },
  })

  const { isPending: isCreateGroupLoading, mutate: createGroupMutation } =
    useMutation({
      mutationFn: createGroupTag,
      onSuccess: () => {
        toast.success('Tạo mới nhóm tags thành công')

        queryClient.invalidateQueries({
          queryKey: [QUERY_KEY.GET_LIST_TAGS],
        })

        handleClose()
      },
      onError: (err) => {
        const { data } = err

        if (data.data && data.data.err_code === 'duplicate_tag') {
          data.data.invalid_idx.forEach((index) => {
            form.setError(`tags.${index}.tag`, { message: 'duplicate_tag' })
          })
        }

        showErrorToast(err.message)
      },
    })

  const { isPending: isEditGroupLoading, mutate: editGroupMutation } =
    useMutation({
      mutationFn: ({ id, payload }) => updateGroupTag(id, payload),
      onSuccess: () => {
        toast.success('Cập nhật nhóm tags thành công')

        queryClient.invalidateQueries({
          queryKey: [QUERY_KEY.GET_LIST_TAGS],
        })

        handleClose()
      },
      onError: (err) => {
        const { data } = err

        if (data.data && data.data.err_code === 'duplicate_tag') {
          data.data.invalid_idx.forEach((index) => {
            form.setError(`tags.${index}.tag`, { message: 'duplicate_tag' })
          })
        }

        showErrorToast(err.message)
      },
    })

  const handleCreateCustomerGroup = (payload) => {
    createGroupMutation(payload)
  }

  const handleEditCustomerGroup = (groupTagId, payload) => {
    editGroupMutation({
      id: groupTagId,
      payload,
    })
  }

  const onSubmit = (fieldsValue) => {
    if (!editGroupTag) {
      handleCreateCustomerGroup({
        name: fieldsValue.name.trim(),
        tags: fieldsValue.tags.map((x) => x.tag.trim()),
      })
    } else {
      handleEditCustomerGroup(editGroupTag._id, {
        name: fieldsValue.name.trim(),
        tags: fieldsValue.tags.map((x) => x.tag.trim()),
      })
    }
  }

  const tagsFieldArray = useFieldArray({
    control: form.control,
    name: 'tags',
  })

  useEffect(() => {
    if (editGroupTag) {
      const formValue = {
        name: editGroupTag.name,
        tags: editGroupTag.raw_tags.map((tag) => ({
          tag,
        })),
      }

      form.reset(formValue)
    }
  }, [editGroupTag, form])

  return (
    <Dialog open={open} onOpenChange={() => handleClose()}>
      <DialogContent className="max-h-[700px] overflow-auto sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {!editGroupTag ? 'Thêm mới nhóm tags' : 'Cập nhật nhóm tags'}
          </DialogTitle>
        </DialogHeader>
        <DialogDescription
          className="hidden"
          aria-describedby="group-tag-dialog"
        />
        <Form {...form}>
          <form ref={formRef} onSubmit={form.handleSubmit(onSubmit)}>
            <div className="grid gap-2">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem className="space-y-1">
                    <Label isRequired>Tên nhóm tags</Label>
                    <FormControl>
                      <Input {...field} placeholder="Nhập tên nhóm tags" />
                    </FormControl>
                    {form.formState.errors && form.formState.errors.name && (
                      <span className="text-sm text-[--label-red-foreground]">
                        {form.formState.errors.name.message}
                      </span>
                    )}
                  </FormItem>
                )}
              />

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Tags</Label>
                  <Plus
                    className="h-4 w-4 cursor-pointer"
                    onClick={(e) => {
                      e.stopPropagation()
                      tagsFieldArray.append({ tag: '' }, { shouldFocus: false })
                    }}
                  />
                </div>
                {tagsFieldArray.fields.map((field, tagFieldIndex) => (
                  <div key={field.id} className="flex items-start gap-2">
                    <FormField
                      control={form.control}
                      name={`tags.${tagFieldIndex}.tag`}
                      render={({ field }) => (
                        <FormItem className="w-full space-y-1">
                          <FormControl>
                            <Input
                              {...field}
                              placeholder="Nhập tên tag"
                              onBlur={() => setFocusFieldIndex(-1)}
                              className={cn(
                                form.formState.errors?.tags?.[tagFieldIndex]
                                  ?.tag?.message &&
                                  focusFieldIndex !== tagFieldIndex &&
                                  'focus-visible:ring-0',
                                form.formState.errors?.tags?.[tagFieldIndex]
                                  ?.tag?.message === 'duplicate_tag' &&
                                  'border-red-500',
                              )}
                              onChange={(e) => {
                                setFocusFieldIndex(tagFieldIndex)

                                field.onChange(e.target.value)
                              }}
                            />
                          </FormControl>
                          {form.formState.errors?.tags?.[tagFieldIndex]?.tag
                            ?.message &&
                            form.formState.errors.tags[tagFieldIndex].tag
                              .message !== 'duplicate_tag' && (
                              <span className="text-sm text-[--label-red-foreground]">
                                {
                                  form.formState.errors.tags[tagFieldIndex].tag
                                    .message
                                }
                              </span>
                            )}
                        </FormItem>
                      )}
                    />
                    <div>
                      <Button
                        variant="outline"
                        type="button"
                        onClick={() => tagsFieldArray.remove(tagFieldIndex)}
                      >
                        <Trash2 className="h-4 w-4 cursor-pointer text-[hsl(var(--icon-danger))]" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </form>
        </Form>
        <DialogFooter className="sm:justify-right">
          <DialogClose asChild>
            <Button
              type="button"
              variant="secondary"
              onClick={() => handleClose()}
            >
              Đóng
            </Button>
          </DialogClose>
          <Button
            type="submit"
            loading={isCreateGroupLoading || isEditGroupLoading}
            onClick={() => formRef.current.requestSubmit()}
          >
            {!editGroupTag ? 'Thêm mới' : 'Cập nhật'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
